import type { BookingAdvance } from '@lemontaigu/pms-core';
import { fmtEuros } from './logic.js';
import { paidCents, paymentStamp } from './departureSheet.js';

// « Arrhes » and « Payé » — what a booking has already paid, worded for the
// planning's side pane and the booking form. Both show the SAME figures and
// tooltip, so they share this and neither re-derives it (the two would drift).
//
// The money is the booking's PAYMENT LIST, the same one the Arrivées and Départs
// sheets total (`paidCents`): Vega's arrhes received, Vega's règlements on the
// booking's invoices, and what the Encaissement form took that Vega does not
// show yet. NOT « the arrhes Vega still holds »: once the guest is invoiced the
// arrhes are consumed and that balance reads 0, though the guest paid — DESSENNES
// 024711 paid 90,66 € on arrival and showed no arrhes at all (2026-10-22).
//
// « Arrhes » keeps its fiscal meaning: only what Vega recorded as arrhes. Every
// other payment is « Autres règlements », so the two are never blended. Arrhes
// held by a guest with several bookings cannot be placed on one, so they are
// named apart and never added in (Hard Rule 1).

/** null while the read is in flight, a message when it failed. */
export type AdvanceRead = BookingAdvance | string | null;

export type PaymentState = 'paid' | 'partial' | 'unpaid';

export interface PaymentSummary {
  /** Payments Vega recorded as arrhes (taken before invoicing). */
  arrhesCents: number;
  /** Every other payment: règlements on the invoice, desk payments not yet in Vega. */
  otherCents: number;
  /** Everything paid towards the booking (signed: a refund reduces it). */
  paidCents: number;
  /** What the booking comes to, as given. */
  totalCents: number;
  /** Still to pay; 0 once paid in full. */
  dueCents: number;
  /** Paid beyond the total; 0 otherwise. */
  overpaidCents: number;
  /** null when there is nothing to pay and nothing paid. */
  state: PaymentState | null;
}

export function paymentSummary(a: BookingAdvance, totalCents: number): PaymentSummary {
  const paid = paidCents(a.payments);
  const arrhes = paidCents(a.payments.filter((p) => p.source === 'arrhes'));
  return {
    arrhesCents: arrhes,
    otherCents: paid - arrhes,
    paidCents: paid,
    totalCents,
    dueCents: Math.max(totalCents - paid, 0),
    overpaidCents: Math.max(paid - totalCents, 0),
    state: paymentState(paid, totalCents),
  };
}

/** The status of a booking from what it has paid and what it comes to: paid in
 *  full (or over), partly, not at all — null when there is nothing to pay and
 *  nothing paid. One rule for the pane, the form and the Réservations column. */
export function paymentState(paid: number, totalCents: number): PaymentState | null {
  return paid > 0 && paid >= totalCents ? 'paid' : paid > 0 ? 'partial' : totalCents > 0 ? 'unpaid' : null;
}

export const PAYMENT_STATE_LABEL_FR: Record<PaymentState, string> = {
  paid: 'Payé',
  partial: 'Partiellement payé',
  unpaid: 'Non payé',
};

/** « 90,66 € », « 0,00 € », « … » while reading, « — » when it could not be read. */
export function amountText(read: AdvanceRead, pick: (s: PaymentSummary) => number, totalCents: number): string {
  if (read === null) return '…';
  if (typeof read === 'string') return '—';
  return fmtEuros(pick(paymentSummary(read, totalCents)));
}

/** The breakdown, for a tooltip: each payment (when, how, how much), what is left
 *  to pay or paid over, then the unplaceable arrhes if any. Null while reading. */
export function paymentTitle(read: AdvanceRead, totalCents: number): string | null {
  if (read === null) return null;
  if (typeof read === 'string') return `Paiements non lus : ${read}`;
  const s = paymentSummary(read, totalCents);
  const lines: string[] = [];
  if (read.payments.length === 0) lines.push('Aucun paiement enregistré');
  for (const p of read.payments) {
    const kind = p.source === 'arrhes' ? 'Arrhes' : p.source === 'reglement' ? 'Règlement' : 'Pris au formulaire, pas encore dans Vega';
    lines.push(`${paymentStamp(p)} · ${p.label ?? '—'} · ${kind} · ${fmtEuros(p.cents)}`);
  }
  lines.push(`Total réservation : ${fmtEuros(s.totalCents)} · payé : ${fmtEuros(s.paidCents)}`);
  if (s.dueCents > 0) lines.push(`Reste à payer : ${fmtEuros(s.dueCents)}`);
  if (s.overpaidCents > 0) lines.push(`Payé en plus du total : ${fmtEuros(s.overpaidCents)}`);
  if (read.depositUnattributedCents !== 0) {
    lines.push(
      `Arrhes du client non rattachées à cette réservation (plusieurs réservations) : ${fmtEuros(read.depositUnattributedCents)} — à vérifier dans Vega`,
    );
  }
  return lines.join('\n');
}
